import { useEffect, useState } from "react"
import Input, { clearForm, FormState, required, submitForm, useRangeInput, useTextInput, useToggleInput } from "../../../../components/input"
import { api } from "../../../../env"
import CreateAnswers, { packAnswers, unpackAnswers } from "./CreateAnswers"
import { AnswerType, QuestionSelector, QuestionSymmetry, QuestionType } from "../../../../utils/enums"

export default function CreateQuestion({onAdd, onCancel, question}) {

    const [state, setState] = useState(FormState.CREATE)
    const [editId, setEditId] = useState(undefined)
    const [answers, setAnswers] = useState([])

    useEffect(() => {
        if (question) {
            setState(FormState.UPDATE)
            setEditId(question.id)
            setAnswers(unpackAnswers(question))
            questionForm.textMentor._setValue(question.textMentor)
            questionForm.textMentee._setValue(question.textMentee)
            questionForm.questionType._setValue(question.questionType)
            questionForm.questionSymmetry._setValue(question.questionSymmetry ?? QuestionSymmetry.BOTH)
            questionForm.answerType._setValue(question.answerType)
            questionForm.weight._setValue(question.weight)
        }
        else reset()
    }, [question])

    const questionForm = {
        textMentor: useTextInput("Question Text for Mentor", "supervisor_account", [required]),
        textMentee: useTextInput("Question Text for Mentee", "person", [required]),
        questionType: useToggleInput("Question Type", "forum", true, [{key: QuestionType.STATIC, label: "Static"}, {key: QuestionType.DYNAMIC, label: "Dynamic (Tracks)"}], "Static"),
        questionSymmetry: useToggleInput("Question Symmetry", "split_scene_left", false, [{key: QuestionSymmetry.BOTH, label: "Ask Mentor & Mentee"}, {key: QuestionSymmetry.MENTOR, label: "Only Mentor"}, {key: QuestionSymmetry.MENTEE, label: "Only Mentee"}], QuestionSymmetry.BOTH),
        answerType: useToggleInput("Answer Type", "speaker_notes", true, [{key: AnswerType.SINGLE, label: "Single Answer"}, {key: AnswerType.MULTI, label: "Multiple Answers"}, {key: AnswerType.TEXT, label: "Free Text"}]),
        weight: useRangeInput("Answer Importance", "release_alert", true, 0, 1, 0.05, 0.7)
    }
    useEffect(() => {
        if (questionForm.questionType._value == QuestionType.STATIC) questionForm.questionSymmetry._setValue(QuestionSymmetry.BOTH)
        else questionForm.questionSymmetry._setValue(question?.questionSymmetry ?? QuestionSymmetry.BOTH)
    }, [questionForm.questionType._value])

    useEffect(() => {
        if (!questionForm.questionSymmetry?._value) return
        const menteeEnabled = [QuestionSymmetry.BOTH, QuestionSymmetry.MENTEE].includes(questionForm.questionSymmetry._value)
        const mentorEnabled = [QuestionSymmetry.BOTH, QuestionSymmetry.MENTOR].includes(questionForm.questionSymmetry._value)
        questionForm.textMentor.setValidators(mentorEnabled ? [required] : [])
        questionForm.textMentee.setValidators(menteeEnabled ? [required] : [])
    }, [questionForm.questionSymmetry._value])

    const reset = () => {
        clearForm(questionForm)
        questionForm.questionType._setValue(QuestionType.STATIC)
        questionForm.weight._setValue(0.7)
        setAnswers([])
        setEditId(undefined)
        setState(FormState.CREATE)
    }

    const submit = () => {
        if (state == FormState.UPDATE) {
            if (editId) saveQuestion()
            else updateQuestion()
        }
        else addQuestion()
    }

    const processQuestion = () => {
        let question = submitForm(questionForm)
        let answersInfo = packAnswers(answers)
        if (!question) return null
        if (question.questionType == QuestionType.DYNAMIC) question.selector = QuestionSelector.TRACKS
        if (question.questionType == QuestionType.STATIC && question.answerType != AnswerType.TEXT && !answersInfo) return null
        if (question.textMentee == "") question.textMentee = question.textMentor
        if (question.textMentor == "") question.textMentor = question.textMentee
        else {
            if (!answersInfo) answersInfo = { answersMentor: undefined, answersMentee: undefined }
            question = {
                ...question,
                ...answersInfo
            }
        } 
        return question
    }

    const addQuestion = () => {
        let question = processQuestion()
        if (!question) return
        onAdd(question)
        reset()
    }

    const updateQuestion = () => {
        let question = processQuestion()
        if (!question) return
        onAdd(question)
    }

    const saveQuestion = () => {
        let question = processQuestion()
        if (!question) return
        api.put(`questionnaires/questions/${editId}`, question)
            .then(_ => onAdd(question))
            .catch(_ => onAdd(question))
    }

    return(<>
        {state == FormState.UPDATE && editId && <div className="flex space-between center v-spacer-xs">
            <p className="section-title-s">Edit Question</p>
            <button className="outline" onClick={onCancel}>Cancel</button>
        </div>}
        <div className="card flex columns gap-xs showing">
            {[QuestionSymmetry.BOTH, QuestionSymmetry.MENTOR].includes(questionForm.questionSymmetry._value) && <Input state={questionForm.textMentor}/>}
            {[QuestionSymmetry.BOTH, QuestionSymmetry.MENTEE].includes(questionForm.questionSymmetry._value) && <Input state={questionForm.textMentee}/>}
            {!editId && <>
                <Input state={questionForm.weight}/>
                <Input state={questionForm.questionType}/>
                {questionForm.questionType._value == QuestionType.DYNAMIC && <Input state={questionForm.questionSymmetry}/>}
                <Input state={questionForm.answerType}/>
            </>}
            {questionForm.questionType._value == QuestionType.STATIC && questionForm.answerType._value != AnswerType.TEXT && <CreateAnswers state={state} isDraft={!editId} answers={answers} answersChanged={setAnswers} />}

            <hr className="vi-spacer-l"/>
            <button className="solid large" onClick={submit}>{state == FormState.UPDATE ? 'Save' : 'Add'} Question</button>
        </div>
    </>)
}